import React, { useState, useEffect } from 'react';
import { isRunningStandalone } from '../utils/platformDetection';

/**
 * OS-specific notification permission and installation modal
 * Replaces the JavaScript alerts in notification-helper.js
 */
const NotificationPrompt = ({ isOpen, onClose, onDismiss, platform }) => {
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [browserName, setBrowserName] = useState('');
  
  // Detect if already running in standalone mode
  const isStandalone = isRunningStandalone();

  useEffect(() => {
    // Detect OS and browser
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    // Override detection with platform prop if provided
    if (platform) {
      const platformStr = typeof platform === 'object' ? platform.platform : platform;
      setIsIOS(platformStr === 'ios');
      setIsAndroid(platformStr === 'android');
    } else {
      // Check for iOS
      const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
      setIsIOS(isIOSDevice);
      
      // Check for Android
      const isAndroidDevice = /Android/.test(userAgent);
      setIsAndroid(isAndroidDevice);
    }
    
    // Detect browser
    let browser = '';
    if (/Chrome/i.test(userAgent)) {
      browser = 'Chrome';
    } else if (/Firefox/i.test(userAgent)) {
      browser = 'Firefox';
    } else if (/Safari/i.test(userAgent)) {
      browser = 'Safari';
    } else if (/Edge|Edg/i.test(userAgent)) {
      browser = 'Edge';
    } else if (/Opera|OPR/i.test(userAgent)) {
      browser = 'Opera';
    } else if (/MSIE|Trident/.test(userAgent)) {
      browser = 'Internet Explorer';
    } else {
      browser = 'Unknown';
    }
    
    setBrowserName(browser);
  }, [platform]);

  // Return null if not open or already in standalone mode for iOS
  if (!isOpen || (isIOS && isStandalone)) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px',
      backdropFilter: 'blur(4px)'
    }}
    onClick={(e) => {
      // Close modal when clicking the backdrop
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}
    >
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        width: '95%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflow: 'auto',
        padding: '24px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
        animation: 'modalFadeIn 0.3s ease-out',
        position: 'relative'
      }}>        
        <button 
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#9ca3af'
          }}
        >
          ×
        </button>
            
        <h2 style={{ 
          margin: '0 0 16px',
          color: '#1f2937',
          fontSize: '20px',
          fontWeight: '600'
        }}>
          {isIOS ? 'Enable Notifications on iOS' : 'Enable Notifications on Android'}
        </h2>
        
        <div style={{ marginBottom: '24px' }}>
          {isIOS ? (
            // iOS-specific content
            <>
              <p style={{ 
                margin: '0 0 16px',
                color: '#4b5563',
                lineHeight: '1.5'
              }}>
                <strong>To receive notifications on iOS, you need to add this app to your home screen.</strong> 
                After installation, launch the app from your home screen to enable notifications.
              </p>
              
              <ol style={{ 
                margin: '0 0 16px',
                paddingLeft: '24px',
                color: '#4b5563',
                lineHeight: '1.7'
              }}>
                <li>Click the <strong>Share</strong> button in Safari (the box with an arrow)</li>
                <li>Scroll down and select <strong>Add to Home Screen</strong></li>
                <li>Tap <strong>Add</strong> in the top right corner</li>
                <li>Launch the app from your home screen</li>
              </ol>
              
              <p style={{ 
                margin: '8px 0 0',
                color: '#4b5563',
                lineHeight: '1.5',
                fontSize: '14px',
                fontStyle: 'italic'
              }}>
                This is required for iOS notifications to work properly with web applications.
              </p>
            </>
          ) : (
            // Android-specific content
            <>
              <p style={{ 
                margin: '0 0 16px',
                color: '#4b5563',
                lineHeight: '1.5'
              }}>
                <strong>Notifications will currently appear in your notification tray.</strong> To enable pop-up alerts:
              </p>

              {browserName === 'Chrome' ? (
                <ol style={{ 
                  margin: '0 0 24px',
                  paddingLeft: '24px',
                  color: '#4b5563',
                  lineHeight: '1.7'
                }}>
                  <li>Tap the <strong>⋮</strong> (three dots) menu</li>
                  <li>Go to <strong>Settings</strong></li>
                  <li>Tap <strong>Notifications</strong></li>
                  <li>Select <strong>In-app notification settings</strong></li>
                  <li>Find <strong>https://www.promptlyyou.com</strong></li>
                  <li>Enable <strong>Show as pop-up</strong></li>
                </ol>
              ) : (
                <ol style={{ 
                  margin: '0 0 24px',
                  paddingLeft: '24px',
                  color: '#4b5563',
                  lineHeight: '1.7'
                }}>
                  <li>Open your browser settings</li>
                  <li>Go to <strong>Notifications</strong> or <strong>Site Settings</strong></li>
                  <li>Find <strong>PromptlyYou</strong></li>
                  <li>Enable <strong>Pop-up notifications</strong></li>
                </ol>
              )}
              
              <p style={{ 
                margin: '8px 0 0',
                color: '#4b5563',
                lineHeight: '1.5',
                fontSize: '14px'
              }}>
                <strong>Note:</strong> If you can't find these settings, notifications will still work but will only appear in your notification tray.
              </p>
              
              <p style={{ 
                margin: '16px 0 0',
                color: '#4b5563',
                lineHeight: '1.5',
                fontSize: '14px',
                fontStyle: 'italic'
              }}>
                You can also install this app to your home screen for a better experience.
                In your browser menu, select "Add to Home Screen" or "Install App".
              </p>
            </>
          )}
        </div>
             
        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <button
            onClick={onDismiss}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f3f4f6',
              color: '#6b7280', 
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontWeight: '500',
              cursor: 'pointer',
              minWidth: '120px'
            }}
          >
            Don't show again
          </button>
          
          <button
            onClick={() => {
              // Just close without setting any localStorage flags
              onClose();
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              minWidth: '120px'
            }}
          >
            Got it
          </button>
        </div>
      </div>
      
      <style>{`
        @keyframes modalFadeIn {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default NotificationPrompt;
